/**
 * OrganizationSelector - Sélecteur d'organisation pour le header
 * Permet de switcher entre organisations si l'utilisateur en a plusieurs
 */

import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check, Crown, ShieldCheck, Shield, Plus } from 'lucide-react';
import config from '../config';

function OrganizationSelector({ token, activeOrgId, onSelectOrg, onCreateOrg }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Charger les organisations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${config.API_URL}/api/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success) {
          setOrganizations(data.organizations);
          // Sélectionner la première par défaut si pas d'active
          if (data.organizations.length > 0 && !activeOrgId) {
            onSelectOrg(data.organizations[0].id);
          }
        }
      } catch (err) {
        console.error('Erreur chargement organisations:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, [token, activeOrgId, onSelectOrg]);

  // Fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtenir l'icône du rôle
  const getRoleIcon = (role) => {
    switch (role) {
      case 'OWNER':
        return Crown;
      case 'ADMIN':
        return ShieldCheck;
      default:
        return Shield;
    }
  };

  // Obtenir la couleur du rôle
  const getRoleColor = (role) => {
    switch (role) {
      case 'OWNER':
        return '#f59e0b';
      case 'ADMIN':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  // Ne pas afficher si pas d'organisations
  if (loading || organizations.length === 0) {
    return null;
  }

  const activeOrg = organizations.find(o => o.id === activeOrgId);

  // Si une seule organisation, afficher juste le badge cliquable
  if (organizations.length === 1) {
    return (
      <button
        onClick={onCreateOrg}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.15s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
        title="Gérer l'organisation"
      >
        {activeOrg?.logo ? (
          <img
            src={activeOrg.logo}
            alt={activeOrg.name}
            style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'cover', pointerEvents: 'none' }}
          />
        ) : (
          <Building2 size={16} color="#6b7280" style={{ pointerEvents: 'none' }} />
        )}
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151', pointerEvents: 'none' }}>
          {activeOrg?.name}
        </span>
      </button>
    );
  }

  // Dropdown pour plusieurs organisations
  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          backgroundColor: isOpen ? '#e5e7eb' : '#f3f4f6',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'background-color 0.15s'
        }}
      >
        {activeOrg?.logo ? (
          <img
            src={activeOrg.logo}
            alt={activeOrg.name}
            style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'cover' }}
          />
        ) : (
          <Building2 size={16} color="#6b7280" />
        )}
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeOrg?.name || 'Organisation'}
        </span>
        <ChevronDown
          size={16}
          color="#6b7280"
          style={{
            transition: 'transform 0.15s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          minWidth: '220px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e5e7eb',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          <div style={{ padding: '8px' }}>
            {organizations.map((org) => {
              const RoleIcon = getRoleIcon(org.role);
              const isActive = org.id === activeOrgId;

              return (
                <button
                  key={org.id}
                  onClick={() => {
                    onSelectOrg(org.id);
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    backgroundColor: isActive ? '#FAF9F5' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.target.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {org.logo ? (
                    <img
                      src={org.logo}
                      alt={org.name}
                      style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Building2 size={14} color="#6b7280" />
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: isActive ? '600' : '500',
                      color: isActive ? '#1a1a1a' : '#374151',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {org.name}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      color: getRoleColor(org.role)
                    }}>
                      <RoleIcon size={10} />
                      {org.role === 'OWNER' ? 'Propriétaire' : org.role === 'ADMIN' ? 'Admin' : 'Membre'}
                      <span style={{ color: '#9ca3af', marginLeft: '4px' }}>
                        ({org.memberCount} membre{org.memberCount !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </div>

                  {isActive && (
                    <Check size={16} color="#1a1a1a" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Option créer une organisation */}
          {onCreateOrg && (
            <>
              <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
              <div style={{ padding: '8px' }}>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onCreateOrg();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: '#1a1a1a'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#FAF9F5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    backgroundColor: '#FAF9F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Plus size={14} color="#1a1a1a" />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Créer une organisation
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default OrganizationSelector;
